import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    AlertTriangle,
    CheckCircle2,
    Target,
    Kanban,
    Link2,
    Clock,
    ArrowRight,
    Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { usePageTitle } from '@/hooks/usePageTitle';

export default function AnalyticsPage() {
    const { t } = useTranslation();
    usePageTitle('nav.analytics');
    const { workspace } = useAuthStore();

    const { data: overview, isLoading: overviewLoading } = useQuery({
        queryKey: ['analytics-overview', workspace?.id],
        queryFn: () => api.getAnalyticsOverview(workspace!.id),
        enabled: !!workspace?.id,
    });

    const { data: drift, isLoading: driftLoading } = useQuery({
        queryKey: ['analytics-drift', workspace?.id],
        queryFn: () => api.getDriftAnalysis(workspace!.id),
        enabled: !!workspace?.id,
    });

    // Helper to translate backend recommendation to Vietnamese
    const translateRecommendation = (rec: string): string => {
        const translations: Record<string, string> = {
            'On track - keep up the momentum!': 'Đang đúng tiến độ - hãy tiếp tục phát huy!',
            'Consider breaking down into smaller tasks or adjusting timeline': 'Cân nhắc chia nhỏ thành các tasks hoặc điều chỉnh timeline',
            'Immediate attention required - review goals and resources': 'Cần xử lý ngay - xem lại mục tiêu và nguồn lực',
            'Slightly behind schedule - monitor closely': 'Hơi chậm tiến độ - cần theo dõi sát sao',
        };
        return translations[rec] || rec;
    };

    const isLoading = overviewLoading || driftLoading;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const stats = overview?.data;
    const driftData = drift?.data;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">{t('analytics.title')}</h1>
                <p className="text-neutral-600 dark:text-neutral-400">
                    {t('analytics.overview')}
                </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard
                    title={t('analytics.okrProgress')}
                    value={`${Math.round((stats?.objectives.average_progress || 0) * 100)}%`}
                    subtitle={`${stats?.objectives.total || 0} ${t('dashboard.objectives').toLowerCase()}`}
                    icon={Target}
                    color="primary"
                />
                <MetricCard
                    title={t('analytics.taskCompletion')}
                    value={`${Math.round((stats?.tasks.completion_rate || 0) * 100)}%`}
                    subtitle={`${stats?.tasks.completed || 0}/${stats?.tasks.total || 0} ${t('common.tasks')}`}
                    icon={CheckCircle2}
                    color="success"
                />
                <MetricCard
                    title={t('analytics.alignmentScore')}
                    value={`${Math.round((stats?.alignment.score || 0) * 100)}%`}
                    subtitle={`${stats?.alignment.linked_tasks || 0} ${t('analytics.linkedTasks').toLowerCase()}`}
                    icon={Link2}
                    color={stats?.alignment.score && stats.alignment.score > 0.7 ? 'success' : 'warning'}
                />
                <MetricCard
                    title={t('analytics.overdueTasks')}
                    value={(stats?.tasks.overdue || 0).toString()}
                    subtitle={t('analytics.needAttention')}
                    icon={Clock}
                    color={stats?.tasks.overdue && stats.tasks.overdue > 0 ? 'danger' : 'success'}
                />
            </div>

            {/* Drift Detection */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Objectives at Risk */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-warning-500" />
                            {t('analytics.driftDetection')}
                        </h2>
                        <div className="flex gap-2 text-xs">
                            <span className="px-2 py-1 bg-danger-50 text-danger-600 rounded">
                                {driftData?.summary.high_risk || 0} {t('analytics.high')}
                            </span>
                            <span className="px-2 py-1 bg-warning-50 text-warning-600 rounded">
                                {driftData?.summary.medium_risk || 0} {t('analytics.medium')}
                            </span>
                        </div>
                    </div>

                    {/* Formula Explanation */}
                    <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-600/10 rounded-lg text-sm">
                        <p className="font-medium text-primary-700 dark:text-primary-300 mb-2">
                            📊 Cách tính độ lệch hướng:
                        </p>
                        <ul className="text-primary-600 dark:text-primary-400 space-y-1 text-xs">
                            <li><strong>Tiến độ kỳ vọng</strong> = Số ngày đã trôi qua / Tổng số ngày × 100%</li>
                            <li><strong>Tiến độ thực tế</strong> = Tổng % hoàn thành các Key Results</li>
                            <li><strong>Độ lệch</strong> = Tiến độ kỳ vọng - Tiến độ thực tế</li>
                            <li className="pt-1">
                                <span className="text-danger-600">🔴 Cao:</span> Lệch &gt;30% |
                                <span className="text-warning-600 ml-2">🟡 Trung bình:</span> Lệch 15-30% |
                                <span className="text-success-600 ml-2">🟢 Thấp:</span> Lệch &lt;15%
                            </li>
                        </ul>
                    </div>

                    {!driftData?.objectives_at_risk?.length ? (
                        <div className="text-center py-8">
                            <CheckCircle2 className="w-12 h-12 text-success-500 mx-auto mb-3" />
                            <p className="text-success-600 font-medium">{t('analytics.allOnTrack')}</p>
                            <p className="text-sm text-neutral-500 mt-1">{t('analytics.noSignificantDrift')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {driftData.objectives_at_risk.slice(0, 5).map((obj) => (
                                <div
                                    key={obj.objective_id}
                                    className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate">{obj.objective_title}</p>
                                            <p className="text-xs text-neutral-500 mt-1">
                                                Còn {obj.days_remaining} ngày
                                            </p>
                                        </div>
                                        <span className={clsx(
                                            'text-xs px-2 py-1 rounded',
                                            obj.severity === 'high' && 'bg-danger-50 text-danger-600',
                                            obj.severity === 'medium' && 'bg-warning-50 text-warning-600',
                                        )}>
                                            Chậm {Math.round(obj.drift_amount * 100)}%
                                        </span>
                                    </div>

                                    {/* Progress comparison */}
                                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                            <span className="text-neutral-500">Kỳ vọng:</span>
                                            <div className="progress-bar mt-1">
                                                <div
                                                    className="progress-bar-fill bg-neutral-400"
                                                    style={{ width: `${Math.round(obj.expected_progress * 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-neutral-500">Thực tế:</span>
                                            <div className="progress-bar mt-1">
                                                <div
                                                    className="progress-bar-fill bg-primary-500"
                                                    style={{ width: `${Math.round(obj.actual_progress * 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-xs text-neutral-500 mt-2 italic">
                                        💡 {translateRecommendation(obj.recommendation)}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Unlinked Tasks */}
                <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                            <Kanban className="w-5 h-5 text-secondary-600" />
                            {t('analytics.unlinkedTasks')}
                        </h2>
                        <span className="text-xs px-2 py-1 bg-neutral-100 dark:bg-neutral-700 rounded">
                            {driftData?.summary.unlinked_task_count || 0} {t('common.tasks')}
                        </span>
                    </div>

                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                        {t('task.noLinkedKRs')}
                    </p>

                    {!driftData?.unlinked_tasks?.length ? (
                        <div className="text-center py-8">
                            <Link2 className="w-12 h-12 text-success-500 mx-auto mb-3" />
                            <p className="text-success-600 font-medium">{t('analytics.allAligned')}</p>
                            <p className="text-sm text-neutral-500 mt-1">{t('analytics.everyTaskLinked')}</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {driftData.unlinked_tasks.slice(0, 6).map((task) => (
                                <div
                                    key={task.id}
                                    className="flex items-center justify-between p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded"
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">{task.title}</p>
                                        <p className="text-xs text-neutral-500">{task.board_name}</p>
                                    </div>
                                    <PriorityBadge priority={task.priority} />
                                </div>
                            ))}

                            {driftData.unlinked_tasks.length > 6 && (
                                <Link
                                    to="/boards"
                                    className="flex items-center justify-center gap-1 text-sm text-primary-600 hover:text-primary-700 py-2"
                                >
                                    {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MetricCard({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
}: {
    title: string;
    value: string;
    subtitle: string;
    icon: any;
    color: 'primary' | 'success' | 'warning' | 'danger';
}) {
    const colorClasses = {
        primary: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        success: 'bg-success-50 text-success-600 dark:bg-success-600/20',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
        danger: 'bg-danger-50 text-danger-600 dark:bg-danger-600/20',
    };

    return (
        <div className="card p-4">
            <div className="flex items-center gap-3">
                <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-xs text-neutral-500">{subtitle}</p>
                </div>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">{title}</p>
        </div>
    );
}

function PriorityBadge({ priority }: { priority: string }) {
    const classes = {
        low: 'bg-neutral-100 text-neutral-600',
        medium: 'bg-primary-50 text-primary-600',
        high: 'bg-warning-50 text-warning-600',
        urgent: 'bg-danger-50 text-danger-600',
    };

    const labels: Record<string, string> = {
        low: 'Thấp',
        medium: 'Trung bình',
        high: 'Cao',
        urgent: 'Khẩn cấp',
    };

    return (
        <span className={clsx('text-xs px-2 py-0.5 rounded', classes[priority as keyof typeof classes] || classes.medium)}>
            {labels[priority] || priority}
        </span>
    );
}
